export type ParallelModeConversion = 'off' | 'to_major' | 'to_minor';

type KeyMode = 'major' | 'minor';

interface KeyContext {
  tonicPitchClass: number;
  sourceMode: KeyMode;
}

const STEP_TO_PITCH_CLASS: Record<string, number> = {
  C: 0,
  D: 2,
  E: 4,
  F: 5,
  G: 7,
  A: 9,
  B: 11,
};

const mod = (value: number, divisor: number): number => {
  return ((value % divisor) + divisor) % divisor;
};

const normalizeMode = (modeText: string | null | undefined): KeyMode | null => {
  if (!modeText) {
    return null;
  }
  const trimmed = modeText.trim().toLowerCase();
  if (trimmed === 'major') {
    return 'major';
  }
  if (trimmed === 'minor') {
    return 'minor';
  }
  return null;
};

const getDirectChild = (element: Element, tagName: string): Element | null => {
  for (const child of Array.from(element.children)) {
    if (child.tagName === tagName) {
      return child;
    }
  }
  return null;
};

const targetModeForConversion = (
  sourceMode: KeyMode,
  conversion: ParallelModeConversion,
): KeyMode => {
  if (conversion === 'to_major') {
    return 'major';
  }
  if (conversion === 'to_minor') {
    return 'minor';
  }
  return sourceMode;
};

const tonicPitchClassFromFifths = (fifths: number, mode: KeyMode): number => {
  const majorPitchClass = mod(7 * fifths, 12);
  if (mode === 'major') {
    return majorPitchClass;
  }
  // Minor tonic is a minor third below the relative major.
  return mod(majorPitchClass + 9, 12);
};

const transformedFifths = (fifths: number, sourceMode: KeyMode, targetMode: KeyMode): number => {
  if (sourceMode === targetMode) {
    return fifths;
  }
  // Parallel major/minor conversion keeps tonic and changes key signature by 3 fifth steps.
  if (sourceMode === 'major' && targetMode === 'minor') {
    return fifths - 3;
  }
  if (sourceMode === 'minor' && targetMode === 'major') {
    return fifths + 3;
  }
  return fifths;
};

const shouldAdjustPitchClass = (
  intervalPitchClass: number,
  sourceMode: KeyMode,
  conversion: ParallelModeConversion,
): boolean => {
  if (conversion === 'to_major' && sourceMode === 'minor') {
    return intervalPitchClass === 3 || intervalPitchClass === 8 || intervalPitchClass === 10;
  }
  if (conversion === 'to_minor' && sourceMode === 'major') {
    return intervalPitchClass === 4 || intervalPitchClass === 9 || intervalPitchClass === 11;
  }
  return false;
};

const getConversionDelta = (sourceMode: KeyMode, conversion: ParallelModeConversion): number => {
  if (conversion === 'to_major' && sourceMode === 'minor') {
    return 1;
  }
  if (conversion === 'to_minor' && sourceMode === 'major') {
    return -1;
  }
  return 0;
};

const applyKeyConversionAndGetContext = (
  keyElement: Element,
  conversion: ParallelModeConversion,
  fallbackMode: KeyMode | null,
): KeyContext | null => {
  const fifthsElement = getDirectChild(keyElement, 'fifths');
  if (!fifthsElement?.textContent) {
    return null;
  }

  const parsedFifths = Number.parseInt(fifthsElement.textContent, 10);
  if (Number.isNaN(parsedFifths)) {
    return null;
  }

  const modeElement = getDirectChild(keyElement, 'mode');
  const sourceMode = normalizeMode(modeElement?.textContent) ?? fallbackMode ?? 'major';
  const targetMode = targetModeForConversion(sourceMode, conversion);
  const nextFifths = transformedFifths(parsedFifths, sourceMode, targetMode);

  if (nextFifths !== parsedFifths) {
    fifthsElement.textContent = `${nextFifths}`;
  }

  if (modeElement) {
    modeElement.textContent = targetMode;
  } else if (sourceMode !== targetMode) {
    const createdModeElement = keyElement.ownerDocument.createElement('mode');
    createdModeElement.textContent = targetMode;
    keyElement.appendChild(createdModeElement);
  }

  return {
    tonicPitchClass: tonicPitchClassFromFifths(parsedFifths, sourceMode),
    sourceMode,
  };
};

const transformNotePitch = (
  noteElement: Element,
  keyContext: KeyContext,
  conversion: ParallelModeConversion,
): void => {
  if (getDirectChild(noteElement, 'rest')) {
    return;
  }

  const pitchElement = getDirectChild(noteElement, 'pitch');
  if (!pitchElement) {
    return;
  }

  const stepElement = getDirectChild(pitchElement, 'step');
  const octaveElement = getDirectChild(pitchElement, 'octave');
  if (!stepElement?.textContent || !octaveElement?.textContent) {
    return;
  }

  const basePitchClass = STEP_TO_PITCH_CLASS[stepElement.textContent.trim().toUpperCase()];
  if (basePitchClass === undefined) {
    return;
  }

  const octave = Number.parseInt(octaveElement.textContent, 10);
  if (Number.isNaN(octave)) {
    return;
  }

  const alterElement = getDirectChild(pitchElement, 'alter');
  const alter = alterElement?.textContent ? Number.parseFloat(alterElement.textContent) : 0;
  if (Number.isNaN(alter)) {
    return;
  }

  const absolutePitchClass = mod(basePitchClass + alter, 12);
  const intervalPitchClass = mod(absolutePitchClass - keyContext.tonicPitchClass, 12);
  if (!shouldAdjustPitchClass(intervalPitchClass, keyContext.sourceMode, conversion)) {
    return;
  }

  const delta = getConversionDelta(keyContext.sourceMode, conversion);
  if (delta === 0) {
    return;
  }

  const nextAlter = alter + delta;
  if (nextAlter === 0) {
    if (alterElement) {
      pitchElement.removeChild(alterElement);
    }
  } else if (alterElement) {
    alterElement.textContent = `${nextAlter}`;
  } else {
    const newAlter = pitchElement.ownerDocument.createElement('alter');
    newAlter.textContent = `${nextAlter}`;
    pitchElement.insertBefore(newAlter, octaveElement);
  }

  const accidentalElement = getDirectChild(noteElement, 'accidental');
  if (accidentalElement) {
    noteElement.removeChild(accidentalElement);
  }
};

export const applyParallelModeConversionToMusicXml = (
  musicXml: string,
  conversion: ParallelModeConversion,
): string => {
  if (conversion === 'off') {
    return musicXml;
  }

  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(musicXml, 'application/xml');
  if (xmlDoc.getElementsByTagName('parsererror').length > 0) {
    return musicXml;
  }

  for (const partElement of Array.from(xmlDoc.getElementsByTagName('part'))) {
    let currentKeyContext: KeyContext | null = null;

    for (const measureElement of Array.from(partElement.children)) {
      if (measureElement.tagName !== 'measure') {
        continue;
      }

      for (const childElement of Array.from(measureElement.children)) {
        if (childElement.tagName === 'attributes') {
          for (const keyElement of Array.from(childElement.children)) {
            if (keyElement.tagName !== 'key') {
              continue;
            }
            const nextContext = applyKeyConversionAndGetContext(
              keyElement,
              conversion,
              currentKeyContext?.sourceMode ?? null,
            );
            if (nextContext) {
              currentKeyContext = nextContext;
            }
          }
          continue;
        }

        if (childElement.tagName === 'note' && currentKeyContext) {
          transformNotePitch(childElement, currentKeyContext, conversion);
        }
      }
    }
  }

  return new XMLSerializer().serializeToString(xmlDoc);
};
